import React, { useState, useEffect, useRef } from "react";
import "../styles/NotificationSystem.css";

const NotificationSystem = ({ tasks, drivers, onTaskClick, onDriverClick }) => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioRef = useRef(null);
  const previousTasksRef = useRef({});
  const previousDriversRef = useRef({});

  // Notification types
  const NOTIFICATION_TYPES = {
    URGENT_ORDER: 'urgent_order',
    DRIVER_OFFLINE: 'driver_offline',
    NEW_ORDER: 'new_order',
    ORDER_READY: 'order_ready',
    DRIVER_BATTERY_LOW: 'driver_battery_low',
    LONG_WAIT_TIME: 'long_wait_time'
  };

  // Create notification
  const createNotification = (type, data, priority = 'normal') => {
    const notification = {
      id: Date.now() + Math.random(),
      type,
      data,
      priority,
      timestamp: new Date(),
      read: false
    };

    setNotifications(prev => [notification, ...prev.slice(0, 19)]); // Keep last 20

    // Play sound for high priority notifications
    if (soundEnabled && priority === 'high' && audioRef.current) {
      audioRef.current.play().catch(() => {
        // Ignore audio play errors
      });
    }

    // Show browser notification if permission granted
    if (Notification.permission === 'granted') {
      new Notification(data.title, {
        body: data.message,
        icon: '/favicon.ico',
        tag: notification.id
      });
    }
  };

  // Check for urgent orders (older than 15 minutes)
  const checkUrgentOrders = () => {
    const urgentOrders = tasks.nonaccepter.filter(task => {
      const createdAt = new Date(task.created_at);
      const now = new Date();
      const diffMinutes = (now - createdAt) / (1000 * 60);
      return diffMinutes > 15;
    });

    urgentOrders.forEach(order => {
      const isNewUrgent = !previousTasksRef.current.urgentOrders?.some(
        prevOrder => prevOrder.idcmd === order.idcmd
      );

      if (isNewUrgent) {
        createNotification(NOTIFICATION_TYPES.URGENT_ORDER, {
          title: '🚨 Urgent Order Alert',
          message: `Order ${order.order_id} has been waiting for over 15 minutes`,
          orderId: order.order_id,
          order: order
        }, 'high');
      }
    });

    previousTasksRef.current.urgentOrders = urgentOrders;
  };

  // Check for new orders
  const checkNewOrders = () => {
    const allCurrentTasks = Object.values(tasks).flat();
    const allPreviousTasks = Object.values(previousTasksRef.current.tasks || {}).flat();

    const newOrders = allCurrentTasks.filter(currentTask => 
      !allPreviousTasks.some(prevTask => prevTask.idcmd === currentTask.idcmd)
    );

    newOrders.forEach(order => {
      createNotification(NOTIFICATION_TYPES.NEW_ORDER, {
        title: '📦 New Order Received',
        message: `New order ${order.order_id} from ${order.designation}`,
        orderId: order.order_id,
        order: order
      }, 'normal');
    });

    previousTasksRef.current.tasks = tasks;
  };

  // Check for orders ready for pickup
  const checkReadyOrders = () => {
    const readyOrders = tasks.prete.filter(task => task.prete === 1);
    
    readyOrders.forEach(order => {
      const wasNotReady = !previousTasksRef.current.readyOrders?.some(
        prevOrder => prevOrder.idcmd === order.idcmd && prevOrder.prete === 1
      );

      if (wasNotReady) {
        createNotification(NOTIFICATION_TYPES.ORDER_READY, {
          title: '✅ Order Ready for Pickup',
          message: `Order ${order.order_id} is ready for pickup at ${order.designation}`,
          orderId: order.order_id,
          order: order
        }, 'normal');
      }
    });

    previousTasksRef.current.readyOrders = readyOrders;
  };

  // Check driver status changes
  const checkDriverStatus = () => {
    const allCurrentDrivers = Object.values(drivers).flat();
    const allPreviousDrivers = Object.values(previousDriversRef.current.drivers || {}).flat();

    // Check for drivers going offline
    allPreviousDrivers.forEach(prevDriver => {
      const currentDriver = allCurrentDrivers.find(d => d.uuid === prevDriver.uuid);
      
      if (currentDriver) {
        // Driver went from connected to disconnected
        if (prevDriver.connected == 1 && currentDriver.connected == 0) {
          createNotification(NOTIFICATION_TYPES.DRIVER_OFFLINE, {
            title: '📱 Driver Went Offline',
            message: `${prevDriver.name || prevDriver.nom} has gone offline`,
            driverId: prevDriver.uuid,
            driver: prevDriver
          }, 'high');
        }

        // Check for low battery
        if (currentDriver.battery && parseInt(currentDriver.battery) <= 20) {
          const wasNotLowBattery = !prevDriver.battery || parseInt(prevDriver.battery) > 20;
          
          if (wasNotLowBattery) {
            createNotification(NOTIFICATION_TYPES.DRIVER_BATTERY_LOW, {
              title: '🔋 Low Battery Alert',
              message: `${currentDriver.name || currentDriver.nom} battery is at ${currentDriver.battery}%`,
              driverId: currentDriver.uuid,
              driver: currentDriver
            }, 'normal');
          }
        }
      }
    });

    previousDriversRef.current.drivers = drivers;
  };

  // Check for long wait times
  const checkLongWaitTimes = () => {
    const longWaitOrders = tasks.unassigned.filter(task => {
      const createdAt = new Date(task.created_at);
      const now = new Date();
      const diffMinutes = (now - createdAt) / (1000 * 60);
      return diffMinutes > 10; // Orders waiting for assignment for over 10 minutes
    });

    longWaitOrders.forEach(order => {
      const wasNotLongWait = !previousTasksRef.current.longWaitOrders?.some(
        prevOrder => prevOrder.idcmd === order.idcmd
      );

      if (wasNotLongWait) {
        createNotification(NOTIFICATION_TYPES.LONG_WAIT_TIME, {
          title: '⏰ Long Wait Time',
          message: `Order ${order.order_id} has been unassigned for over 10 minutes`,
          orderId: order.order_id,
          order: order
        }, 'normal');
      }
    });

    previousTasksRef.current.longWaitOrders = longWaitOrders;
  };

  // Main monitoring function
  useEffect(() => {
    const checkAllNotifications = () => {
      checkUrgentOrders();
      checkNewOrders();
      checkReadyOrders();
      checkDriverStatus();
      checkLongWaitTimes();
    };

    // Initial check
    checkAllNotifications();

    // Set up interval for continuous monitoring
    const interval = setInterval(checkAllNotifications, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [tasks, drivers]);

  // Request notification permission
  useEffect(() => {
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // Mark as read
    setNotifications(prev => 
      prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
    );

    // Navigate to relevant item
    if (notification.data.orderId) {
      onTaskClick(notification.data.orderId);
    } else if (notification.data.driverId) {
      onDriverClick({ driverId: notification.data.driverId, driver: notification.data.driver });
    }

    setIsOpen(false);
  };

  // Clear all notifications
  const clearAllNotifications = () => {
    setNotifications([]);
  };

  // Mark all as read
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  // Get notification icon
  const getNotificationIcon = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.URGENT_ORDER:
        return 'fas fa-exclamation-triangle';
      case NOTIFICATION_TYPES.DRIVER_OFFLINE:
        return 'fas fa-user-slash';
      case NOTIFICATION_TYPES.NEW_ORDER:
        return 'fas fa-plus-circle';
      case NOTIFICATION_TYPES.ORDER_READY:
        return 'fas fa-check-circle';
      case NOTIFICATION_TYPES.DRIVER_BATTERY_LOW:
        return 'fas fa-battery-quarter';
      case NOTIFICATION_TYPES.LONG_WAIT_TIME:
        return 'fas fa-clock';
      default:
        return 'fas fa-bell';
    }
  };

  // Get notification priority class
  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'high': return 'high-priority';
      case 'normal': return 'normal-priority';
      default: return 'low-priority';
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="notification-system">
      <audio ref={audioRef} preload="auto">
        <source src="/notification.mp3" type="audio/mpeg" />
      </audio>

      <button 
        className={`notification-bell ${unreadCount > 0 ? 'has-notifications' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Notifications"
      >
        <i className="fas fa-bell"></i>
        {unreadCount > 0 && <span className="notification-count">{unreadCount}</span>}
      </button>

      {isOpen && (
        <div className="notification-panel">
          <div className="notification-header">
            <h4>Notifications</h4>
            <div className="notification-controls">
              <button 
                className={`sound-toggle ${soundEnabled ? 'enabled' : 'disabled'}`}
                onClick={() => setSoundEnabled(!soundEnabled)}
                title={soundEnabled ? 'Sound ON' : 'Sound OFF'}
              >
                <i className={`fas ${soundEnabled ? 'fa-volume-up' : 'fa-volume-mute'}`}></i>
              </button>
              {notifications.length > 0 && (
                <>
                  <button onClick={markAllAsRead} title="Mark All as Read">
                    <i className="fas fa-check-double"></i>
                  </button>
                  <button onClick={clearAllNotifications} title="Clear All">
                    <i className="fas fa-trash"></i>
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="no-notifications">
                <i className="fas fa-bell-slash"></i>
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div 
                  key={notification.id}
                  className={`notification-item ${getPriorityClass(notification.priority)} ${!notification.read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    <i className={getNotificationIcon(notification.type)}></i>
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">{notification.data.title}</div>
                    <div className="notification-message">{notification.data.message}</div>
                    <div className="notification-time">
                      {notification.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                  {!notification.read && <div className="unread-indicator"></div>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSystem;
